/**
 * @file PricingHistoryModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-17
 * @modified 2026-11-03
 * @description Pricing & Costing: twelve months of cost, sale and margin for
 *              one customer and product, each distinct combination once with
 *              how many lines had it (Cam, Review D5 note: "unique only with
 *              count"). Costs and margins only with costs.view. Read only.
 *
 *              1.1.0 (2026-11-03, Cam — a note on a hand-typed figure): the
 *              notes typed with the figures confirmed on a group's lines are
 *              shown with it (a cost's only with costs.view — the server
 *              leaves them out otherwise).
 */

import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import { API_URL } from '../config';
import { formatDate } from '../utils/dateFormatters';
import { money } from '../utils/pricingCosting';
import './PricingCosting.css';
import './PricingHistoryModal.css';

function PricingHistoryModal({ accountId, itemCode, title, seeCosts, onClose }) {
  const [state, setState] = useState({ loading: true, error: null, groups: [], lines: 0 });
  const anyNotes = state.groups.some(g => (g.notes || []).length > 0);

  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ accountId: String(accountId), itemCode });
        const res = await fetch(`${API_URL}/api/pricing-costing/history?${qs}`, { credentials: 'include' });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error || 'The history could not be loaded');
        if (!gone) setState({ loading: false, error: null, groups: body.groups || [], lines: body.lines || 0 });
      } catch (err) {
        if (!gone) setState({ loading: false, error: err.message, groups: [], lines: 0 });
      }
    })();
    return () => { gone = true; };
  }, [accountId, itemCode]);

  return (
    <Modal isOpen onClose={onClose} title={`Last 12 months — ${title}`} size="lg" className="pricing-history">
      {/* Padded inside the scroller, never on it: the sticky header must sit at
          its top (PricingHistoryModal.css). */}
      <div className="pricing-history-body">
      {state.loading ? <LoadingSpinner /> : state.error ? (
        <p className="pc-flag err"><span className="material-symbols-rounded">error</span>{state.error}</p>
      ) : !state.groups.length ? (
        <p className="pc-muted">Nothing sold to them for this product in the last 12 months.</p>
      ) : (
        <>
          <p className="pc-muted">{state.lines} line{state.lines === 1 ? '' : 's'} — each different cost and price once, newest first. Invoices made in Xero carry no cost.</p>
          <table className="data-table">
            <thead>
              <tr>
                {seeCosts && <th className="num">Cost</th>}
                <th className="num">Sale</th>
                {seeCosts && <th className="num">Margin</th>}
                <th className="num">Lines</th>
                <th>When</th>
                <th>Orders / invoices</th>
                {anyNotes && <th>Notes</th>}
              </tr>
            </thead>
            <tbody>
              {state.groups.map((g, i) => (
                <tr key={i}>
                  {seeCosts && <td className="num">{g.cost === null ? '—' : money(g.cost)}</td>}
                  <td className="num">{money(g.sale)}</td>
                  {seeCosts && (
                    <td className={`num ${g.margin !== null && g.margin < 0 ? 'pc-neg' : ''}`}>
                      {g.margin === null ? '—' : `${money(g.margin)}${g.margin_pc !== null ? ` (${g.margin_pc.toFixed(1)}%)` : ''}`}
                    </td>
                  )}
                  <td className="num">{g.count}</td>
                  <td>{g.first === g.last ? formatDate(g.last) : `${formatDate(g.first)} – ${formatDate(g.last)}`}</td>
                  <td className="pc-muted">{g.refs.join(', ')}{g.count > g.refs.length ? ' …' : ''}</td>
                  {anyNotes && (
                    <td className="pricing-history-notes">
                      {(g.notes || []).map((n, j) => (
                        <span key={j} className="pc-src pc-note-shown" title={[n.side === 'cost' ? 'Cost note' : 'Price note', n.by ? `by ${n.by}` : null, n.at ? formatDate(n.at) : null, n.ref].filter(Boolean).join(' · ')}>
                          <span className="material-symbols-rounded">sticky_note_2</span>
                          {n.side === 'cost' ? 'Cost' : 'Price'}: {n.note}
                        </span>
                      ))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      </div>
    </Modal>
  );
}

export default PricingHistoryModal;
